/**
 * useCcsApi — thin API layer + shared autosave primitives for the
 * Custom Creative Session (CCS) UI.
 *
 * Mirrors the app-wide axios convention (see OfferSearchSelect.jsx /
 * CreativeHub.jsx): base URL from process.env.REACT_APP_BACKEND_URL,
 * cookie auth via withCredentials (also set globally in App.js). No new
 * HTTP client is introduced.
 *
 * Everything lives under /api/ccs. Each method resolves to the parsed
 * response body.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CCS = `${API}/ccs`;
// withCredentials is already the axios default in App.js; we pass it
// explicitly too, matching OfferSearchSelect.jsx.
const CFG = { withCredentials: true };

export function useCcsApi() {
  return useMemo(() => {
    const body = (r) => r.data;
    return {
      createSession: (offer_id) =>
        axios.post(`${CCS}/sessions`, { offer_id }, CFG).then(body),
      getSession: (id) =>
        axios.get(`${CCS}/sessions/${id}`, CFG).then(body),
      getContext: (id) =>
        axios.get(`${CCS}/sessions/${id}/context`, CFG).then(body),
      saveDraft: (id, patch) =>
        axios.patch(`${CCS}/sessions/${id}/draft`, patch, CFG).then(body),
      sendChat: (id, message) =>
        axios.post(`${CCS}/sessions/${id}/chat`, { message }, CFG).then(body),
      getMessages: (id) =>
        axios.get(`${CCS}/sessions/${id}/messages`, CFG).then(body),
      proceed: (id) =>
        axios.post(`${CCS}/sessions/${id}/proceed`, {}, CFG).then(body),
      back: (id) =>
        axios.post(`${CCS}/sessions/${id}/back`, {}, CFG).then(body),
      addArtifact: (id, kind, text) =>
        axios.post(`${CCS}/sessions/${id}/artifacts`, { kind, text }, CFG).then(body),
      updateArtifact: (id, artifactId, text) =>
        axios.patch(`${CCS}/sessions/${id}/artifacts/${artifactId}`, { text }, CFG).then(body),
      deleteArtifact: (id, artifactId) =>
        axios.delete(`${CCS}/sessions/${id}/artifacts/${artifactId}`, CFG).then(body),
      saveBrief: (id, patch) =>
        axios.patch(`${CCS}/sessions/${id}/brief`, patch, CFG).then(body),
      submit: (id) =>
        axios.post(`${CCS}/sessions/${id}/submit`, {}, CFG).then(body),
    };
  }, []);
}

/**
 * useAutosave — debounced save with a status the UI can surface.
 * status: 'idle' | 'saving' | 'saved' | 'error'.
 * - schedule(value): debounce a save of `value`.
 * - flush(): save any pending value immediately (call on blur / before nav).
 * - retry(): re-run the last failed save.
 */
export function useAutosave(saveFn, delay = 800) {
  const [status, setStatus] = useState('idle');
  const timerRef = useRef(null);
  const pendingRef = useRef(undefined);
  const failedRef = useRef(undefined);
  const saveRef = useRef(saveFn);

  useEffect(() => { saveRef.current = saveFn; }, [saveFn]);

  const doSave = useCallback(async (value) => {
    setStatus('saving');
    try {
      await saveRef.current(value);
      failedRef.current = undefined;
      setStatus('saved');
    } catch (e) {
      failedRef.current = value;
      setStatus('error');
    }
  }, []);

  const flush = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (pendingRef.current !== undefined) {
      const v = pendingRef.current;
      pendingRef.current = undefined;
      doSave(v);
    }
  }, [doSave]);

  const schedule = useCallback((value) => {
    pendingRef.current = value;
    setStatus('saving');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const v = pendingRef.current;
      pendingRef.current = undefined;
      if (v !== undefined) doSave(v);
    }, delay);
  }, [doSave, delay]);

  /** Save right now, with no debounce. For controls that have no typing to
   *  debounce and no blur to flush — a <select>, a checkbox, a radio. Using
   *  `schedule` for those only opens a window in which the choice can be lost. */
  const saveNow = useCallback((value) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    pendingRef.current = undefined;
    doSave(value);
  }, [doSave]);

  const retry = useCallback(() => {
    if (failedRef.current !== undefined) doSave(failedRef.current);
  }, [doSave]);

  // FLUSH ON UNMOUNT, do not cancel.
  //
  // BUG, found live 2026-09-08. This was
  // `clearTimeout(timerRef.current)` alone — it threw the pending save away.
  // A <select> has no blur to flush it, so picking a video format and leaving
  // within the 800 ms debounce lost the choice silently: the checklist ticked
  // on screen while ZERO requests were sent. Reproduced before fixing.
  //
  // `saveRef.current` is used rather than `doSave` because the component is
  // unmounting — `setStatus` on a dead component does nothing useful, and the
  // request must still go out. A failure here cannot be shown to anyone (there
  // is no longer a screen), so it is logged rather than swallowed.
  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = undefined;
    if (pending !== undefined) {
      try {
        Promise.resolve(saveRef.current(pending)).catch((e) => {
          console.error("Autosave lost on unmount:", e);
        });
      } catch (e) {
        console.error("Autosave lost on unmount:", e);
      }
    }
  }, []);

  return { status, schedule, flush, saveNow, retry };
}

/** Small inline save-status indicator shared by every autosaving field. */
export function SaveIndicator({ status, onRetry }) {
  if (!status || status === 'idle') return null;
  if (status === 'saving') {
    return <span className="text-xs text-slate-500" data-testid="save-status">Saving…</span>;
  }
  if (status === 'saved') {
    return <span className="text-xs text-emerald-500" data-testid="save-status">Saved</span>;
  }
  if (status === 'error') {
    return (
      <span className="text-xs text-rose-400" data-testid="save-status">
        Couldn't save —{' '}
        <button
          type="button"
          onClick={onRetry}
          className="underline hover:text-rose-300"
          data-testid="save-retry"
        >
          Retry
        </button>
      </span>
    );
  }
  return null;
}

/**
 * AutosaveTextarea — self-contained editable field that debounce-saves via
 * `saveFn(text)` and shows a SaveIndicator. Uncontrolled after mount (its
 * value is local), so key it by a stable id when the underlying record can
 * change identity. Used for artifact alternates, brief and production notes.
 */
export function AutosaveTextarea({
  initialValue = '',
  saveFn,
  label,
  placeholder,
  rows = 3,
  testId,
  disabled = false,
  headerRight = null,
}) {
  const [value, setValue] = useState(initialValue);
  const { status, schedule, flush, retry } = useAutosave(saveFn);

  return (
    <div className="space-y-1">
      {(label || headerRight) && (
        <div className="flex items-center justify-between gap-2">
          {label ? (
            <label className="text-xs font-medium text-slate-400">{label}</label>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <SaveIndicator status={status} onRetry={retry} />
            {headerRight}
          </div>
        </div>
      )}
      <textarea
        value={value}
        disabled={disabled}
        onChange={(e) => { setValue(e.target.value); schedule(e.target.value); }}
        onBlur={flush}
        placeholder={placeholder}
        rows={rows}
        className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-y"
        data-testid={testId}
      />
    </div>
  );
}
